import { useEffect, useMemo, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createMemoryHistory } from '@tanstack/react-router';
import { I18nextProvider } from 'react-i18next';
import type { i18n as I18n } from 'i18next';
import { Toaster } from 'sonner';
import { useMediaQuery } from './lib/media.ts';
import { TooltipProvider } from './components/ui/tooltip.tsx';
import { HelpProvider } from './help/help.tsx';
import { onApiResponse } from './lib/api.ts';
import { meQueryKey } from './auth/session.ts';
import { createAppRouter } from './router.tsx';
import { ErrorBoundary } from './shell/errors.tsx';
import { watchForNewVersion } from './shell/stale.ts';

/** Toasts sit above the bottom tabs on phones. */
function AppToaster() {
  const wide = useMediaQuery('(min-width: 768px)');
  return <Toaster position={wide ? 'bottom-center' : 'top-center'} />;
}

export function createQueryClient() {
  return new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true } } });
}

/** The whole app. Tests pass a memory path; the browser uses its address bar. */
export function App({
  i18n,
  initialPath,
  queryClient: given,
  version,
}: {
  i18n: I18n;
  initialPath?: string;
  queryClient?: QueryClient;
  /** The web build's version; tests set it to check the new-version message. */
  version?: string;
}) {
  const [queryClient] = useState(() => given ?? createQueryClient());
  const router = useMemo(
    () => createAppRouter(queryClient, initialPath ? createMemoryHistory({ initialEntries: [initialPath] }) : undefined),
    [queryClient, initialPath],
  );

  useEffect(() => watchForNewVersion(i18n.t, version), [i18n, version]);
  useEffect(
    () =>
      // A 401 on any call means the session ended: go to the sign-in screen.
      onApiResponse((response) => {
        const path = router.state.location.pathname;
        if (response.status === 401 && path !== '/sign-in' && !new URL(response.url, 'http://x').pathname.startsWith('/api/auth/')) {
          queryClient.setQueryData(meQueryKey, null);
          void router.navigate({ to: '/sign-in' });
        }
      }),
    [router, queryClient],
  );

  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={300}>
            <HelpProvider>
              <RouterProvider router={router} />
              <AppToaster />
            </HelpProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}
